import React from "react";
import "./Strategies.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLaptopCode,
  faChartLine,
  faBullhorn,
  faGears,
  faUsers,
  faDatabase,
} from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";

const strategyAreas = [
  {
    title: "Web & Experience Strategy",
    icon: faLaptopCode,
    focus:
      "High-converting websites, landing pages and funnels that align with your offer, audience and buyer journey.",
    services: [
      "Website & landing page design",
      "Conversion-focused funnel setup",
      "Performance & SEO foundations",
    ],
    tag: "Web & Funnels",
  },
  {
    title: "CRM & Revenue Operations",
    icon: faChartLine,
    focus:
      "CRM platforms that sales, marketing and support teams actually use – wired for clean data and predictable revenue.",
    services: [
      "CRM implementation (Salesforce, HubSpot, Dynamics, Zoho, custom)",
      "Pipeline & playbook design",
      "Automation, scoring & reporting",
    ],
    tag: "CRM & RevOps",
  },
  {
    title: "Accounting & Bookkeeping Backbone",
    icon: faDatabase,
    focus:
      "Clean, US-compliant books that give you an always-on financial truth for pricing, hiring and growth decisions.",
    services: [
      "Monthly bookkeeping & reconciliations",
      "Cash-flow & margin visibility",
      "Tax-ready books & CPA collaboration",
    ],
    tag: "Finance Ops",
  },
  {
    title: "Automation & Systems Integration",
    icon: faGears,
    focus:
      "Connecting your tools so leads, customers and finance data move automatically instead of via spreadsheets.",
    services: [
      "No-code & low-code automations",
      "API & middleware integrations",
      "Data syncing between CRM, web & finance",
    ],
    tag: "Automation",
  },
  {
    title: "Marketing & Demand Generation",
    icon: faBullhorn,
    focus:
      "Bringing in the right traffic and leads, then nurturing them with content, email and retargeting.",
    services: [
      "Campaign & funnel strategy",
      "Email, SMS & retargeting journeys",
      "Analytics & optimization loops",
    ],
    tag: "Growth",
  },
  {
    title: "People, Support & Client Experience",
    icon: faUsers,
    focus:
      "Support, onboarding and account management flows that keep customers happy and reduce churn.",
    services: [
      "Onboarding & activation journeys",
      "Support workflows & SLAs",
      "Feedback loops & NPS/CSAT insights",
    ],
    tag: "CX & Support",
  },
];

const processPillars = [
  {
    title: "01. Understand",
    desc: "We map offers, audience, current tools and constraints so every strategy is grounded in reality.",
  },
  {
    title: "02. Design",
    desc: "We prioritise quick wins and long-term foundations across web, CRM, finance and marketing.",
  },
  {
    title: "03. Implement",
    desc: "We ship in small, testable phases – wiring systems, playbooks and reporting together.",
  },
  {
    title: "04. Optimize",
    desc: "We review numbers monthly and keep iterating to improve revenue, margins and experience.",
  },
];

/* Motion variants */
const sectionVariants = {
  hidden: { opacity: 0, y: 32 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const gridVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: "easeOut" },
  },
};

const Strategies = () => {
  return (
    <motion.section
      className="strat-section"
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      variants={sectionVariants}
    >
      <div className="strat-inner">
        {/* Header */}
        <motion.header className="strat-header" variants={sectionVariants}>
          <p className="strat-eyebrow">SERVICE STRATEGY</p>
          <h1 className="strat-title">How We Design Strategy Across Services</h1>
          <p className="strat-sub">
            Instead of treating your website, CRM, accounting and marketing as
            separate projects, we design a connected strategy – so what you say,
            track and measure across the funnel actually matches how you make
            money.
          </p>

          <div className="strat-pill-row">
            <span className="strat-pill">Full-funnel thinking</span>
            <span className="strat-pill">Systems before hacks</span>
            <span className="strat-pill">Numbers over opinions</span>
          </div>
        </motion.header>

        {/* Strategy Areas */}
        <motion.section
          className="strat-block"
          variants={sectionVariants}
          viewport={{ once: true, amount: 0.2 }}
        >
          <div className="strat-block-header">
            <h2 className="strat-section-title">
              Strategy lenses for each service area
            </h2>
            <p className="strat-section-sub">
              Every engagement plugs into one or more of these strategy lenses –
              whether we&apos;re building a new CRM, cleaning up books, redesigning
              a site or wiring automations between them.
            </p>
          </div>

          <motion.div className="strat-grid" variants={gridVariants}>
            {strategyAreas.map((area, idx) => (
              <motion.article
                key={idx}
                className="strat-card"
                variants={cardVariants}
                whileHover={{ y: -6, scale: 1.01 }}
                transition={{ type: "spring", stiffness: 220, damping: 20 }}
              >
                <div className="strat-card-top">
                  <div className="strat-icon">
                    <FontAwesomeIcon icon={area.icon} />
                  </div>
                  <span className="strat-tag">{area.tag}</span>
                </div>
                <h3 className="strat-card-title">{area.title}</h3>
                <p className="strat-card-focus">{area.focus}</p>
                <ul className="strat-list">
                  {area.services.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </motion.article>
            ))}
          </motion.div>
        </motion.section>

        {/* Process strip */}
        <motion.section
          className="strat-process"
          variants={sectionVariants}
          viewport={{ once: true, amount: 0.2 }}
        >
          <div className="strat-process-header">
            <h2 className="strat-section-title">
              A simple, repeatable way we run strategy
            </h2>
            <p className="strat-section-sub">
              Whether it&apos;s accounting, CRM development or web & marketing,
              you&apos;ll recognise the same clear phases, communication and decision
              points.
            </p>
          </div>

          <motion.div className="strat-process-grid" variants={gridVariants}>
            {processPillars.map((pillar, idx) => (
              <motion.div
                key={idx}
                className="strat-process-card"
                variants={cardVariants}
                whileHover={{ y: -4 }}
                transition={{ type: "spring", stiffness: 210, damping: 22 }}
              >
                <p className="strat-process-step">{pillar.title}</p>
                <p className="strat-process-desc">{pillar.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </motion.section>
      </div>
    </motion.section>
  );
};

export default Strategies;
